import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { prepareSelect } from "../../services/core/Select";
import { useTranslation } from "react-i18next";
import { guardianTypesManagement } from "../../hooks/api/GuardianTypesManagement/GuardianTypesManagement";
import { studentGuardianManagement } from "../../hooks/api/StudentGuardianManagement";
import { studentAddressManagement } from "../../hooks/api/StudentAddressManagement/StudentAddressManagement";
import { municipalitiesManagement } from "../../hooks/api/MunicipalitiesManagement/MunicipalitiesManagement";

const EditGuardian = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { guardianId } = useParams();
    const [formErrors, setFormErrors] = useState([]);

    const [guardianTypes, setGuardianTypes] = useState([]);
    const { useGuardianTypesListMutation } = guardianTypesManagement;
    const [guardianTypesList] = useGuardianTypesListMutation();

    const { useUpdateGuardianMutation, useViewGuardianMutation } = studentGuardianManagement;
    const [updateGuardian] = useUpdateGuardianMutation();
    const [viewGuardian] = useViewGuardianMutation();

    const [address, setAddress] = useState([]);
    const [municipalities, setMunicipalities] = useState([]);
    const { useStudentAddressListMutation } = studentAddressManagement;
    const { useMunicipalitiesListMutation } = municipalitiesManagement;
    const [studentAddressList] = useStudentAddressListMutation();
    const [municipalitiesList] = useMunicipalitiesListMutation();

    const tabs = [
        { title: t("details"), isActive: true },
        { title: t("guardians.students"), onClick: () => gotoStudentsTab(guardianId) },
    ];

    const gotoStudentsTab = (guardianId) => {
        navigate("/guardians/" + guardianId + "/students");
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values };
        updateGuardian({ body, guardianId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadGuardian();
            }
        });
    };

    const loadGuardian = () => {
        viewGuardian({guardianId}).then((result) => {
            formRef.current.setValues(result?.data);
        });
    }

    useEffect(() => {
        loadGuardian();
        guardianTypesList().then((result) => {
            let options = [];
            result?.data?.records.map((guardianType) => {
                options.push({
                    id: guardianType.id,
                    name: guardianType.name,
                });
            });
            setGuardianTypes(prepareSelect(options));
        });

        studentAddressList().then((result) => {
            let options = [];
            result?.data?.records.map((school) => {
                options.push({
                    id: school.name,
                    name: school.name,
                });
            });
            setAddress(prepareSelect(options));
        });

        municipalitiesList().then((result) => {
            let options = [];
            result?.data?.records.map((municipality) => {
                options.push({
                    id: municipality.id,
                    name: municipality.name,
                    postalCode: municipality.postalCode,
                });
            });
            setMunicipalities(prepareSelect(options));
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/guardians" />
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("guardians.title")} icon="fa-user-graduate" />
                    <Link title={t("details")} />
                    <Link title={t("guardians.editGuardian")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        firstName: "",
                        lastName: "",
                        email: "",
                        phoneNumber: "",
                        idCardNumber: "",
                        type: null,
                        municipality: null,
                        address: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Input
                                        name="username"
                                        label={t("students.username")}
                                        required={true}
                                        value={values.username}
                                        onChange={handleChange}
                                        error={formErrors?.username}
                                        hint={"Korisničko ime će se koristiti za login staratelja"}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="firstName"
                                        label={t("students.firstName")}
                                        required={true}
                                        value={values.firstName}
                                        onChange={handleChange}
                                        error={formErrors?.firstName}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="lastName"
                                        label={t("students.lastName")}
                                        required={true}
                                        value={values.lastName}
                                        onChange={handleChange}
                                        error={formErrors?.lastName}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="email"
                                        label={t("employees.email")}
                                        required={true}
                                        value={values.email}
                                        onChange={handleChange}
                                        error={formErrors?.email}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="phoneNumber"
                                        label={t("employees.phoneNumber")}
                                        required={true}
                                        value={values.phoneNumber}
                                        onChange={handleChange}
                                        error={formErrors?.phoneNumber}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="municipality"
                                        label={t("students.municipality")}
                                        required={true}
                                        options={municipalities}
                                        onChange={handleChange}
                                        selectedValue={values.municipality}
                                        error={formErrors?.municipality}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="address"
                                        label={t("students.address")}
                                        required={true}
                                        options={address}
                                        onChange={handleChange}
                                        selectedValue={values.address}
                                        error={formErrors?.address}
                                        hint={t("hintForAddress")}
                                        allowAppendText={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="idCardNumber"
                                        label={t("students.idNumber")}
                                        value={values.idCardNumber}
                                        onChange={handleChange}
                                        error={formErrors?.idCardNumber}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="type"
                                        label={t("students.guardianType")}
                                        required={true}
                                        options={guardianTypes}
                                        onChange={handleChange}
                                        selectedValue={values.type}
                                        error={formErrors?.type}
                                        searchVisible={false}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditGuardian;
